import * as Api from '@ViewModels';
import * as React from 'react';
import { SaveStatus } from '../../../../../shared/components/automation/SaveStatus';
import { StepSendFromOption } from '../../../../components/automation/AutomationStepCard/components/StepSendFromOption';
import { OpportunityStepForm } from '../../components/OpportunityStepForm';
import { ScheduleEditor } from '../../components/ScheduleEditor';
import { StepAdvancedOptionsPanel } from '../../components/StepAdvancedOptionsPanel';
import { useOpportunityStep } from '../../hooks/useOpportunityStep';
import type { AutomationStepNodeData } from '../../types/stepNodes';
import { ISelectBoxOption } from '../../../../components/SelectBox';
import { useStepRenderer } from './hooks/useStepRenderer';
import { useAutomationFlowContext } from '../../contexts/AutomationFlowContext';
import { ImpersonationContextViewModel } from '../../../../../viewmodels/AdminViewModels';

interface OpportunityStepRendererProps {
	nodeId: string;
	data: AutomationStepNodeData;
	readOnly?: boolean;
}

export function OpportunityStepRenderer({ nodeId, data, readOnly: readOnlyProp }: OpportunityStepRendererProps) {
	const {
		step: opportunityStep,
		template,
		canEdit,
		readonly,
		trigger,
		errorMessages,
		stepData,
		stepId,
		impersonationContext,
	} = useStepRenderer<Api.IOpportunityAutomationStep>(data, readOnlyProp);

	const { updateNodeData, connectionTypes } = useAutomationFlowContext();
	const stepType = data.stepType;
	const schedule = opportunityStep?.schedule || stepData?.schedule;

	const impersonationContextViewModel = React.useMemo(
		() => (impersonationContext ? new ImpersonationContextViewModel(impersonationContext) : undefined),
		[impersonationContext]
	);

	const {
		selectedBoard,
		setSelectedBoard,
		selectedStage,
		setSelectedStage,
		dealSize,
		setDealSize,
		updateExisting,
		setUpdateExisting,
		nameFormat,
		setNameFormat,
		boardOptions,
		stageOptions,
		skipStepContactFilter,
		setSkipStepContactFilter,
		advancedOptionsToggled,
		setAdvancedOptionsToggled,
		autoSave,
		clearInlineErrorMessage,
		isLoadingBoards,
		save,
	} = useOpportunityStep({
		opportunityStep,
		template,
		stepId: stepId || '',
		readonly,
		onSaveError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const syncStepToNodeData = (updatedFields: Partial<Api.IOpportunityAutomationStep>) => {
		if (!updateNodeData || !opportunityStep) return;
		updateNodeData(nodeId, {
			automationStep: { ...opportunityStep, ...updatedFields },
		});
	};

	const handleBoardChange = (board: Api.IBoard, firstStage: Api.IBoardStage | null) => {
		setSelectedBoard(board);
		setSelectedStage(firstStage);
		save({ newBoard: board, newStage: firstStage || undefined });
		syncStepToNodeData({ boardReference: board, boardStageReference: firstStage || undefined });
	};

	const handleStageChange = (stage: Api.IBoardStage) => {
		setSelectedStage(stage);
		save({ newStage: stage });
		syncStepToNodeData({ boardStageReference: stage });
	};

	const handleDealSizeChange = (size: string) => {
		setDealSize(size);
		clearInlineErrorMessage();
	};

	const handleDealSizeBlur = () => {
		save({ newDealSize: dealSize });
		syncStepToNodeData({ dealSize: dealSize ? parseFloat(dealSize) : undefined });
	};

	const handleUpdateExistingChange = (checked: boolean) => {
		setUpdateExisting(checked);
		if (checked) {
			setDealSize('');
		}
		save({ newUpdateExisting: checked });
		syncStepToNodeData({ updateExistingOpportunity: checked });
	};

	const handleNameFormatChange = (value: Api.OpportunityNameFormat) => {
		setNameFormat(value);
		save({ newNameFormat: value });
		syncStepToNodeData({ nameFormat: value });
	};

	const handleScheduleChange = (newSchedule: Api.IAutomationStepSchedule | null) => {
		if (!newSchedule) return;
		save({ newSchedule });
		syncStepToNodeData({ schedule: newSchedule });
	};

	const handleSaveSenderOptions = (option: Api.ISendFromOptions | null, _sendToSender?: boolean) => {
		save({ newSendFrom: option });
		syncStepToNodeData({ sendFromOptions: option });
	};

	const handleFilterChange = (filter: Api.IContactsFilterRequest | null) => {
		setSkipStepContactFilter(filter);
		save({ newContactFilter: filter });
		syncStepToNodeData({ contactFilter: filter ?? undefined });
	};

	const formattedStageOptions: ISelectBoxOption<Api.IBoardStage>[] = stageOptions.map(opt => ({
		...opt,
		title: typeof opt.title === 'string' ? opt.title : opt.value.name,
	}));

	const baseKey = `${stepType}-${nodeId}-${opportunityStep?.id || ''}`;

	return (
		<>
			<SaveStatus
				status={autoSave.saveStatus}
				variant='simple'
				retryCount={autoSave.retryCount}
				errorMessage={autoSave.errorMessage}
			/>
			{opportunityStep && (
				<ScheduleEditor
					schedule={schedule}
					trigger={trigger}
					stepType={stepType}
					canEdit={canEdit}
					isBusy={autoSave.saveStatus === 'saving'}
					step={opportunityStep}
					index={0}
					onScheduleChange={handleScheduleChange}
					parentCaseInfo={data.parentCaseInfo}
					impersonationContext={impersonationContext}
				/>
			)}
			<OpportunityStepForm
				selectedBoard={selectedBoard}
				selectedStage={selectedStage}
				dealSize={dealSize}
				updateExisting={updateExisting}
				nameFormat={nameFormat}
				boardOptions={boardOptions}
				stageOptions={formattedStageOptions}
				canEdit={canEdit}
				isBusy={autoSave.saveStatus === 'saving'}
				isLoadingBoards={isLoadingBoards}
				baseKey={baseKey}
				onBoardChange={handleBoardChange}
				onStageChange={handleStageChange}
				onDealSizeChange={handleDealSizeChange}
				onDealSizeBlur={handleDealSizeBlur}
				onUpdateExistingChange={handleUpdateExistingChange}
				onNameFormatChange={handleNameFormatChange}
				onClearError={clearInlineErrorMessage}
			/>
			<StepAdvancedOptionsPanel
				isExpanded={advancedOptionsToggled}
				onToggle={() => setAdvancedOptionsToggled(!advancedOptionsToggled)}
				stepId={stepId || nodeId}
				filter={skipStepContactFilter}
				canEdit={canEdit}
				isBusy={autoSave.saveStatus === 'saving'}
				onFilterChange={handleFilterChange}
			>
				{opportunityStep && (
					<StepSendFromOption
						step={opportunityStep}
						onSave={handleSaveSenderOptions}
						connectionTypes={connectionTypes}
						sendFromLabel='Always assign this opportunity to'
						canEdit={canEdit}
						impersonationContext={impersonationContextViewModel}
					/>
				)}
			</StepAdvancedOptionsPanel>
		</>
	);
}
